<template>
  <v-app class="login-layout">
    <v-app-bar
      color="#3ACAC9"
      class="text-white login-app-bar"
      :height="60"
      flat
    >
      <v-toolbar-title class="login-app-bar-title">
        KP相談カルテ
      </v-toolbar-title>
    </v-app-bar>
    <v-main
      theme="light"
      class="c-login"
    >
      <div class="login-page">
        <v-card
          class="login-card"
          variant="flat"
        >
          <h1 class="login-card__title">
            Kids Public 相談員ログイン
          </h1>
          <MaintenanceNotice
            class="login-card__notice"
            :body="maintenanceNoticeBody"
          />
          <div
            v-if="message != ''"
            class="notice login-failure"
            role="alert"
            v-html="message"
          ></div>
          <!-- ステージング等でメールログインが許可されている場合のみ、Googleログインの上にID/パスワード入力欄を出す -->
          <section
            v-if="emailLoginEnabled"
            class="login-method login-method--email"
          >
            <h2 class="login-method__title">
              メールアドレス・パスワードでログイン
            </h2>
            <v-form class="login-method__form">
              <v-text-field
                v-model="email"
                class="my-2"
                color="primary"
                variant="underlined"
                density="compact"
                prepend-icon="mdi-account"
                name="email"
                label="メールアドレス"
                type="text"
              />
              <v-text-field
                v-model="password"
                color="primary"
                bg-color="white"
                variant="underlined"
                density="compact"
                prepend-icon="mdi-lock"
                name="password"
                label="パスワード ※英数字20文字以上"
                type="password"
              />
            </v-form>
            <v-btn
              class="login-method__submit"
              color="teal"
              @click="onLogin()"
            >
              ログイン
            </v-btn>
            <v-divider class="login-method__divider" />
          </section>
          <section class="login-method login-method--google">
            <p class="login-method__lead">
              「ログイン」ボタンをクリックし、Kids Publicから発行されたメールアドレスを選択してください。
            </p>
            <GoogleLogin @message-updated="updateMessage" />
          </section>
          <div class="login-help">
            <section class="login-help__card">
              <h2 class="login-help__title">
                <v-icon
                  icon="mdi-help-circle"
                  size="18"
                  class="login-help__icon"
                />
                ログインでお困りの方へ
              </h2>
              <p class="login-help__text">
                ログインできない場合や、ご不明な点がございましたら、下記フォームよりお問い合わせください。
              </p>
              <a
                href="https://docs.google.com/forms/d/e/1FAIpQLScQ6DgPrOLT36UHJ7-9Q9JiP-A6NR3Gza3UbZMN07gTq5C95Q/viewform"
                class="login-help__link"
              >相談員専用問い合わせフォーム</a>
            </section>
            <section class="login-help__card">
              <h2 class="login-help__title">
                <v-icon
                  icon="mdi-account-question"
                  size="18"
                  class="login-help__icon"
                />
                このログイン画面にお心当たりのない方へ
              </h2>
              <p class="login-help__text">
                こちらは、産婦人科・小児科オンラインの医療従事者専用のログイン画面です。ご不明な点は下記よりお問い合わせください。
              </p>
              <a
                href="https://docs.google.com/forms/d/e/1FAIpQLSeQIKiN1wcWuQ3UAje_vR8rCYSMf56djFf6yFRXHvgrVg1BJQ/viewform?c=0&w=1"
                class="login-help__link"
              >利用者様問い合わせフォーム</a>
            </section>
          </div>
        </v-card>
      </div>
    </v-main>
  </v-app>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import GoogleLogin from "./GoogleLogin.vue";
import { MaintenanceNotice } from "@/vue/entities/maintenance-notice";

export default {
  name: "App",
  components: {
    GoogleLogin,
    MaintenanceNotice
  },
  emits: ["setUpNotice", "setUpLogoutTimer"],
  data: () => ({
    email: "",
    password: "",
    redirectPath: "",
    message: ""
  }),
  computed: {
    ...mapGetters(["loggedIn", "emailLoginEnabled", "maintenanceNoticeBody"]),
  },
  methods: {
    ...mapActions(["login", "fetchAuthSetting"]),
    onLogin: function() {
      this.login({ email: this.email,
        password: this.password,
        router: this.$router,
        redirectPath: this.redirectPath }
      ).then((result) => {
        if(result?.loggedIn == false){
          if (result?.error?.code === "account_inactive") {
            this.message = result.error.message;
          } else {
            this.message = "ログインに失敗しました";
          }
        } else {
          this.$emit("setUpNotice");
          this.$emit("setUpLogoutTimer");
        }
      });
    },
    updateMessage(newMessage) {
      this.message = newMessage;
    },
  },
  created() {
    this.redirectPath = this.$route.query.redirect_to;
  },
  mounted() {
    this.fetchAuthSetting();
  },
};
</script>

<style lang="scss">
$login-accent: #33b5b4;
$login-text: #222;
// デザイン案（Figma）のフォント。Webフォントは layouts/application.html.erb で読み込む
$login-font-family: "Noto Sans JP", "Hiragino Kaku Gothic ProN", "Hiragino Sans", Meiryo, sans-serif;

// Vuetify の .v-application の font-family より優先させるため、同じ要素のクラスを重ねる
.v-application.login-layout {
  font-family: $login-font-family;
}

.login-app-bar {
  .v-toolbar__content {
    align-items: stretch;
    padding-top: 0;
    padding-bottom: 0;

    // Vuetify の .v-toolbar__content > .v-toolbar-title（余白・font-size・font-weight）より優先させるため、ここに置く
    > .login-app-bar-title {
      display: flex;
      align-items: center;
      height: 100%;
      margin-inline-start: 20px;
      font-size: 18px;
      font-weight: 700;
      letter-spacing: 0.1em;
    }
  }
}

.c-login {
  background-color: #f8f8f8;
  color: $login-text;

  .login-page {
    padding: 28px 16px 24px;
  }

  .login-card {
    max-width: 1000px;
    margin: 0 auto;
    padding: 24px;
    color: $login-text !important;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }

  .login-card__title {
    margin-bottom: 16px;
    font-size: 20px;
    font-weight: 700;
    line-height: 1.4;
    text-align: center;
  }

  .login-card__notice {
    margin-bottom: 0;
  }

  .notice {
    font-size: 1.2rem;
    font-weight: bold;
    line-height: 1.8;
  }

  .login-failure {
    margin-top: 16px;
    color: rgb(var(--v-theme-alertRed));
    text-align: center;
  }

  .login-method {
    max-width: 640px;
    margin: 40px auto 0;
    text-align: center;
  }

  .login-method__title {
    margin-bottom: 8px;
    font-size: 16px;
    font-weight: 700;
  }

  .login-method__form {
    max-width: 400px;
    margin: 0 auto;
    text-align: left;
  }

  .login-method__submit {
    display: block;
    width: 200px;
    margin: 8px auto 0;
    font-size: 18px;
  }

  .login-method__divider {
    margin-top: 32px;
  }

  .login-method--email + .login-method--google {
    margin-top: 24px;
  }

  .login-method__lead {
    margin-bottom: 12px;
    font-size: 14px;
  }

  .v-field__input {
    padding: 8px 0;
  }

  .v-input--horizontal .v-input__prepend {
    margin-inline-end: 9px;
  }

  .login-help {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
    margin-top: 48px;
    padding: 16px;
    background-color: #effafa;
    border-radius: 4px;
  }

  .login-help__card {
    padding: 16px;
    background-color: #fff;
    border-radius: 4px;
  }

  .login-help__title {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-bottom: 6px;
    font-size: 14px;
    font-weight: 700;
  }

  .login-help__icon {
    color: $login-accent;
  }

  .login-help__text {
    margin-bottom: 8px;
    font-size: 14px;
    line-height: 1.5;
  }

  .login-help__link {
    display: inline-block;
    padding: 2px 8px;
    font-size: 12px;
    color: $login-accent;
    text-decoration: none;
    border: 1px solid $login-accent;
    border-radius: 2px;

    &:hover {
      background-color: rgba(51, 181, 180, 0.08);
    }
  }

  // Vuetify の md（960px）未満では案内カードを1カラムにする（NFR-202）
  @media (max-width: 959px) {
    .login-help {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 600px) {
    .login-card {
      padding: 16px;
    }
  }
}
</style>
